import { usePayments } from "@/features/payments/hooks/usePayments";
import { calculateTotal } from "@/features/payments/lib/utils";
import { client } from "@/lib/hono";
import toast from "@/lib/toast";
import { useRouter } from "next/navigation";
import { MutableRefObject, useState } from "react";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { useAuth } from "@/features/auth/hooks/useAuth";

interface ErrorMessage {
    param: string;
    message: string;
}

interface ErrorResponse {
    messages: ErrorMessage[];
}

interface TransactionResponse {
    success: boolean;
    [key: string]: any;
}

interface Response {
    transaction_response?: TransactionResponse;
    [key: string]: any;
}

export const useCharge = ({
    fieldsRef,
}: {
    fieldsRef: MutableRefObject<any>;
}) => {
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [closePaymentModal] = usePayments((state) => [
        state.closePaymentModal,
    ]);
    const addFundsPayload = usePayments((state) => state.addFundsPayload);
    const installments = usePayments((state) => state.installments);
    const [needsInvoice, setNeedsInvoice] = useState(false);

    const { user } = useAuth();
    const router = useRouter();
    const { locale } = useLocale();

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const nameForInvoice = needsInvoice
            ? ((formData.get("nameForInvoice") || "") as string)
            : "";
        const idForInvoice = needsInvoice
            ? ((formData.get("idForInvoice") || "") as string)
            : "";

        chargeCcData({ nameForInvoice, idForInvoice });
    };

    const chargeCcData = async ({
        nameForInvoice,
        idForInvoice,
    }: {
        nameForInvoice: string;
        idForInvoice: string;
    }) => {
        if (loading) return;

        if (!addFundsPayload) {
            setErrorMessage(t("payments.errors.unexpected-contact-support"));
            return;
        }

        const chargedAmount = calculateTotal(addFundsPayload);
        const company =
            nameForInvoice || idForInvoice
                ? `${nameForInvoice}${nameForInvoice && idForInvoice ? " - " : ""}${idForInvoice}`
                : "";

        if (!fieldsRef.current) {
            setErrorMessage(t("payments.errors.unexpected-contact-support"));
            return;
        }

        setLoading(true);

        // Step 1: Get handshake token from server
        const handshakeRes = await client.api.billing.handshake.$post({
            json: { amount: chargedAmount },
        });
        if (!handshakeRes.ok) {
            setErrorMessage(t("payments.errors.unexpected-contact-support"));
            setLoading(false);
            return;
        }
        const { thtk } = await handshakeRes.json();

        // Step 2: Charge via Tranzila hosted fields
        const installmentParams =
            installments > 1
                ? {
                      payment_plan: "8",
                      total_installments_number: installments.toString(),
                      first_installment_amount: (
                          Math.ceil((chargedAmount * 100) / installments) / 100
                      ).toString(),
                      other_installments_amount: (
                          Math.floor((chargedAmount * 100) / installments) / 100
                      ).toString(),
                  }
                : { payment_plan: "1" };

        fieldsRef.current.charge(
            {
                terminal_name: process.env.NEXT_PUBLIC_TRANZILA_TERMINAL_NAME,
                amount: chargedAmount.toString(),
                thtk,
                response_language: locale === "he" ? "hebrew" : "english",
                email: user?.email,
                contact: user?.name,
                ...(company && { company }),
                ...installmentParams,
            },
            async function (
                err: ErrorResponse | null,
                response: Response | null
            ) {
                if (err) {
                    await handleErrors(err);
                }
                if (response) {
                    await parseResponse(
                        response,
                        chargedAmount,
                        company,
                        nameForInvoice,
                        idForInvoice
                    );
                }
                setLoading(false);
            }
        );
    };

    const handleErrors = async (error: ErrorResponse) => {
        error.messages.forEach(function (message: ErrorMessage) {
            const errorElement = document.getElementById(
                "errors_for_" + message.param
            );
            if (errorElement) {
                errorElement.textContent = message.message;
            }
        });
    };

    const parseResponse = async (
        response: Response,
        chargedAmount: number,
        company: string,
        nameForInvoice: string,
        idForInvoice: string
    ) => {
        if (
            response.transaction_response &&
            response.transaction_response.success
        ) {
            const recordResponse = await client.api.billing.funding.$post({
                json: {
                    amounts: {
                        storage: addFundsPayload!.storage.amount,
                        bandwidth: addFundsPayload!.bandwidth.amount,
                        credits: addFundsPayload!.credits.amount,
                    },
                    chargedAmount,
                    tranzilaTransactionId:
                        response.transaction_response.transaction_id,
                    company,
                    nameForInvoice,
                    idForInvoice,
                    installments,
                },
            });

            if (!recordResponse.ok) {
                setErrorMessage(
                    t("payments.errors.unexpected-contact-support")
                );
                return;
            }

            toast.success(t("toast.success.payment-successful"));
            window.dispatchEvent(new CustomEvent("billing:add-funds-paid"));
            router.push("/home");
            closePaymentModal();
        } else {
            console.log("Payment failed:", response);
            setErrorMessage(
                response?.transaction_response?.error ||
                    t("payments.errors.unexpected-contact-support")
            );
        }
    };

    return {
        handleSubmit,
        loading,
        errorMessage,
        needsInvoice,
        setNeedsInvoice,
    };
};
